import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { X, Printer, CheckSquare, Square } from 'lucide-react';
import api from '../utils/api';
import { CLINIC_NAME, CLINIC_ADDRESS, CLINIC_PHONE, CLINIC_EMAIL } from '../utils/clinicConfig';
import { todayISO } from '../utils/clinicDate';

function fmt(n) { return (Number(n)||0).toLocaleString('en-IN', { maximumFractionDigits:2 }); }
function fmtDate(iso) {
  if (!iso) return '';
  const [y,m,d] = (iso||'').slice(0,10).split('-');
  if (!y || !m || !d) return iso;
  return new Date(Number(y),Number(m)-1,Number(d)).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' });
}

// ── PharmacyBillPrintModal ────────────────────────────────────────────────────
// Same clinic header / patient-details-block / print mechanism as BillPrintModal,
// but shows ONLY pharmacy items and pharmacy costs — no treatments, no
// treatment-derived totals of any kind.
export default function PharmacyBillPrintModal({ patient, onClose }) {
  const [patientDetails, setPatientDetails] = useState(patient || {});
  const [allItems, setAllItems]             = useState([]);   // every pharmacy (outgoing) transaction for this patient
  const [billingRecord, setBillingRecord]   = useState(null); // { discount, amountPaid, ... }
  const [loading, setLoading]               = useState(true);
  // Which dates to include in this bill
  const [selectedDates, setSelectedDates]   = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const uid = patient?.patientUID || '';
      const [patRes, pharmRes, billRes] = await Promise.all([
        api.get('/patients', { params: { search: uid, limit: 100 } }),
        api.get('/pharmacy/transactions', { params: { type: 'outgoing', search: uid, limit: 1000 } }),
        api.get(`/pharmacy/billing/${uid}`).catch(() => null),
      ]);
      const patientMatch = (patRes.data.patients || []).find(p => p.patientUID === uid) || patient;
      setPatientDetails(patientMatch || patient || {});
      setBillingRecord(billRes?.data?.billing || null);

      const items = (pharmRes.data.transactions || []).filter(t => t.patientUID === uid);
      items.sort((a,b) => (a.date||'') > (b.date||'') ? 1 : -1);
      setAllItems(items);

      const dates = [...new Set(items.map(t => t.date))];
      setSelectedDates(new Set(dates));
    } catch {
      setPatientDetails(patient || {});
      setAllItems([]);
    } finally {
      setLoading(false);
    }
  }, [patient]);

  useEffect(() => { load(); }, [load]);

  // Manage the print-only container — same pattern as BillPrintModal, so it
  // reuses the exact same @media print / body.print-bill-mode CSS already
  // shipped in global.css.
  const [printContainerEl] = React.useState(() => {
    const el = document.createElement('div');
    el.id = 'bill-print-only';
    document.body.appendChild(el);
    return el;
  });
  useEffect(() => {
    document.body.classList.add('print-bill-mode');
    return () => {
      document.body.classList.remove('print-bill-mode');
      if (printContainerEl.parentNode) printContainerEl.parentNode.removeChild(printContainerEl);
    };
  }, [printContainerEl]);

  const uniqueDates = [...new Set(allItems.map(t => t.date))].sort();

  function toggleDate(d) {
    setSelectedDates(prev => {
      const next = new Set(prev);
      next.has(d) ? next.delete(d) : next.add(d);
      return next;
    });
  }
  function selectAll()  { setSelectedDates(new Set(uniqueDates)); }
  function selectNone() { setSelectedDates(new Set()); }

  const chosenItems = allItems.filter(t => selectedDates.has(t.date));

  // Group chosen items by date for display
  const byDate = {};
  chosenItems.forEach(t => {
    if (!byDate[t.date]) byDate[t.date] = [];
    byDate[t.date].push(t);
  });
  const chosenDatesSorted = Object.keys(byDate).sort();

  const grandTotal = chosenItems.reduce((s,t) => s + (t.sellingPrice||0)*(t.quantity||0), 0);

  // Bill number: PH + 6-digit patient sequence + date YYYYMMDD
  const billNumber = (() => {
    const seq  = (patientDetails.patientUID || patient?.patientUID || 'PT-000000').replace(/\D/g, '').padStart(6, '0');
    const date = todayISO().replace(/-/g, '');
    return `PH${seq}${date}`;
  })();

  function doPrint() {
    const billEl = document.getElementById('bill-print-area');
    if (billEl && printContainerEl) {
      printContainerEl.innerHTML = billEl.innerHTML;
      printContainerEl.style.cssText = 'display:block;width:100%;padding:0;margin:0;background:white;color:black;font-size:12px;line-height:1.5;';
    }
    const prev = document.title;
    document.title = CLINIC_NAME;
    window.print();
    document.title = prev;
    if (printContainerEl) { printContainerEl.innerHTML = ''; printContainerEl.style.cssText = ''; }
  }

  const modal = (
    <div style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(0,0,0,0.65)', display:'flex', flexDirection:'column', alignItems:'center' }}
      onClick={onClose}>
      {/* Toolbar */}
      <div className="no-print" style={{ width:'100%', background:'#1e293b', color:'white', padding:'10px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}
        onClick={e => e.stopPropagation()}>
        <div>
          <div style={{ fontWeight:700, fontSize:14 }}>Print Pharmacy Bill — {patientDetails.name || patient?.name}</div>
          <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>
            {uniqueDates.length > 1 ? `${uniqueDates.length} purchase dates found — select which to include` : 'Pharmacy items only'}
          </div>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button className="btn btn-sm" style={{ background:'#2563eb', color:'white' }} onClick={doPrint}>
            <Printer size={13} /> Print Bill
          </button>
          <button className="btn btn-sm" style={{ background:'#475569', color:'white' }} onClick={onClose}>
            <X size={13} /> Close
          </button>
        </div>
      </div>

      {/* Date selector — only shown when multiple purchase dates exist */}
      {!loading && uniqueDates.length > 1 && (
        <div className="no-print" style={{ width:'210mm', background:'#f8fafc', padding:'12px 16px', borderBottom:'1px solid #e2e8f0', flexShrink:0 }}
          onClick={e => e.stopPropagation()}>
          <div style={{ fontWeight:700, fontSize:12, color:'#475569', marginBottom:8, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span>Select Purchase Dates to Include:</span>
            <div style={{ display:'flex', gap:6 }}>
              <button className="btn btn-sm btn-outline" onClick={selectAll} style={{ fontSize:11 }}>All</button>
              <button className="btn btn-sm btn-outline" onClick={selectNone} style={{ fontSize:11 }}>None</button>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:6 }}>
            {uniqueDates.map((d) => {
              const checked = selectedDates.has(d);
              const dayItems = allItems.filter(t => t.date === d);
              return (
                <label key={d} style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 10px', borderRadius:6, border:`1px solid ${checked ? '#2563eb' : '#e2e8f0'}`, background: checked ? '#eff6ff' : 'white', cursor:'pointer', fontSize:12 }}>
                  {checked ? <CheckSquare size={14} color="#2563eb" /> : <Square size={14} color="#94a3b8" />}
                  <input type="checkbox" checked={checked} onChange={() => toggleDate(d)} style={{ display:'none' }} />
                  <div>
                    <div style={{ fontWeight:700, color: checked ? '#2563eb' : '#1e293b' }}>{fmtDate(d)}</div>
                    <div style={{ color:'#64748b' }}>{dayItems.length} item{dayItems.length!==1?'s':''}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Bill preview */}
      <div style={{ flex:1, overflowY:'auto', width:'100%', display:'flex', justifyContent:'center', padding:'20px 0' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ background:'white', width:'210mm', minHeight:'297mm', boxShadow:'0 4px 32px rgba(0,0,0,0.4)' }}>
          <div id="bill-print-area" style={{ padding:'14mm 16mm', color:'#111', fontSize:12, lineHeight:1.5 }}>

          {/* Clinic header */}
          <div style={{ textAlign:'center', marginBottom:16, borderBottom:'2px solid #1e293b', paddingBottom:12 }}>
            <div style={{ fontSize:22, fontWeight:900, color:'#1e293b', letterSpacing:'-0.02em' }}>{CLINIC_NAME}</div>
            {(CLINIC_ADDRESS || CLINIC_PHONE || CLINIC_EMAIL) && (
              <table style={{ width:'100%', borderCollapse:'collapse', marginTop:6 }}>
                <tbody>
                  <tr>
                    {CLINIC_ADDRESS && (
                      <td style={{ fontSize:11, color:'#475569', textAlign:'center', padding:'1px 8px' }}>
                        📍&nbsp;{CLINIC_ADDRESS}
                      </td>
                    )}
                  </tr>
                  <tr>
                    {CLINIC_PHONE && (
                      <td style={{ fontSize:11, color:'#475569', textAlign:'center', padding:'1px 8px' }}>
                        📱&nbsp;{CLINIC_PHONE}
                        {CLINIC_EMAIL && <>&nbsp;&nbsp;✉&nbsp;{CLINIC_EMAIL}</>}
                      </td>
                    )}
                  </tr>
                </tbody>
              </table>
            )}
            <div style={{ fontSize:11, color:'#64748b', marginTop:6, fontWeight:600, letterSpacing:'0.05em' }}>PHARMACY BILL</div>
          </div>

          {/* Bill meta */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:4, marginBottom:14, fontSize:11 }}>
            <div><span style={{ color:'#64748b' }}>Bill No: </span><strong>{billNumber}</strong></div>
            <div style={{ textAlign:'right' }}><span style={{ color:'#64748b' }}>Date: </span><strong>{new Date().toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</strong></div>
          </div>

          {/* Patient details */}
          <div style={{ marginBottom:14, padding:'10px 14px', border:'1px solid #e2e8f0', borderRadius:6 }}>
            <div style={{ fontSize:15, fontWeight:700, marginBottom:6 }}>{patientDetails.name || patient?.name}</div>
            <table style={{ fontSize:11, color:'#475569', borderCollapse:'collapse', width:'100%' }}>
              <tbody>
                <tr>
                  <td style={{ padding:'1px 12px 1px 0', fontFamily:'monospace', fontWeight:700, color:'#2563eb', whiteSpace:'nowrap' }}>{patientDetails.patientUID || patient?.patientUID}</td>
                  {patientDetails.age && <td style={{ padding:'1px 12px 1px 0', whiteSpace:'nowrap' }}>{patientDetails.age}y,&nbsp;{patientDetails.gender}</td>}
                  {patientDetails.phone && <td style={{ padding:'1px 12px 1px 0', whiteSpace:'nowrap' }}>📱&nbsp;{patientDetails.phone}</td>}
                  {patientDetails.address && <td style={{ padding:'1px 0', whiteSpace:'nowrap' }}>📍&nbsp;{patientDetails.address}</td>}
                </tr>
              </tbody>
            </table>
          </div>

          {loading ? (
            <div style={{ textAlign:'center', color:'#64748b', padding:20 }}>Loading pharmacy purchases…</div>
          ) : chosenDatesSorted.length === 0 ? (
            <div style={{ textAlign:'center', color:'#64748b', padding:20 }}>No pharmacy purchases selected.</div>
          ) : (
            <>
              {/* Pharmacy items grouped by purchase date */}
              {chosenDatesSorted.map((date) => {
                const dayItems  = byDate[date];
                const dayTotal  = dayItems.reduce((s,t) => s+(t.sellingPrice||0)*(t.quantity||0), 0);
                return (
                  <div key={date} style={{ marginBottom:14, border:'1px solid #e2e8f0', borderRadius:6, overflow:'hidden', breakInside:'avoid' }}>
                    <div style={{ padding:'8px 12px', background:'#f8fafc', borderBottom:'1px solid #e2e8f0' }}>
                      <span style={{ fontWeight:700, fontSize:13 }}>{fmtDate(date)}</span>
                    </div>
                    <div style={{ padding:'8px 12px' }}>
                      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
                        <thead>
                          <tr style={{ color:'#94a3b8' }}>
                            <th style={{ textAlign:'left', padding:'2px 0', fontWeight:600 }}>Item</th>
                            <th style={{ textAlign:'right', padding:'2px 0', fontWeight:600 }}>Qty</th>
                            <th style={{ textAlign:'right', padding:'2px 0', fontWeight:600 }}>Rate</th>
                            <th style={{ textAlign:'right', padding:'2px 0', fontWeight:600 }}>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dayItems.map((t) => (
                            <tr key={t._id} style={{ borderTop:'1px solid #f1f5f9' }}>
                              <td style={{ padding:'3px 0', fontWeight:600 }}>{t.itemName}</td>
                              <td style={{ textAlign:'right', padding:'3px 0', color:'#64748b' }}>{t.quantity} {t.unit}</td>
                              <td style={{ textAlign:'right', padding:'3px 0', color:'#64748b' }}>₹{fmt(t.sellingPrice)}</td>
                              <td style={{ textAlign:'right', padding:'3px 0', fontWeight:700, color:'#16a34a' }}>₹{fmt((t.sellingPrice||0)*(t.quantity||0))}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={3} style={{ textAlign:'right', padding:'4px 0', fontWeight:700, fontSize:11, color:'#475569' }}>Date Subtotal</td>
                            <td style={{ textAlign:'right', padding:'4px 0', fontWeight:800, color:'#16a34a' }}>₹{fmt(dayTotal)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                );
              })}

              {/* Discount, if any is applied to this patient's pharmacy account */}
              {(billingRecord?.discount || 0) > 0 && (
                <div style={{ marginTop:10, padding:'8px 12px', border:'1px solid #fbbf24', background:'#fffbeb', borderRadius:6, display:'flex', justifyContent:'space-between', fontSize:12 }}>
                  <span style={{ color:'#92400e' }}>Discount Applied</span>
                  <strong style={{ color:'#92400e' }}>−₹{fmt(billingRecord.discount)}</strong>
                </div>
              )}

              {/* Grand total — pharmacy only */}
              <div style={{ marginTop:10, background:'#1e293b', borderRadius:6, color:'white', overflow:'hidden' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', textAlign:'center' }}>
                  <tbody>
                    <tr>
                      <td style={{ padding:'10px 14px' }}>
                        <div style={{ fontSize:10, color:'#94a3b8', marginBottom:4 }}>
                          {(billingRecord?.discount || 0) > 0 ? 'AMOUNT DUE (AFTER DISCOUNT)' : 'PHARMACY GRAND TOTAL'}
                        </div>
                        <div style={{ fontSize:18, fontWeight:800, color:'white' }}>
                          ₹{fmt(Math.max(grandTotal - (billingRecord?.discount || 0), 0))}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Payments Received — itemized, same as the Treatment Bill.
                  A "Balance Adjustment" line makes up any gap left by admin
                  corrections (which overwrite amountPaid directly and log a
                  ₹0 audit note instead of a real payment), so this list
                  always sums to the same figure shown in the grand total. */}
              {(() => {
                const entries   = (billingRecord?.paymentHistory || []).filter(e => (e.amount||0) > 0);
                const entrySum  = entries.reduce((s,e) => s + (e.amount||0), 0);
                const actualPaid = billingRecord?.amountPaid || 0;
                const gap = actualPaid - entrySum;
                const payments = [...entries];
                if (Math.abs(gap) > 0.5) {
                  payments.push({ amount: gap, method: 'adjustment', note: 'Balance adjustment', paidAt: null });
                }
                if (payments.length === 0) return null;
                return (
                  <div style={{ marginTop:14, border:'1px solid #e2e8f0', borderRadius:6, overflow:'hidden' }}>
                    <div style={{ padding:'8px 12px', background:'#f8fafc', borderBottom:'1px solid #e2e8f0' }}>
                      <span style={{ fontWeight:700, fontSize:12, color:'#1e293b' }}>Payments Received</span>
                    </div>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
                      <thead>
                        <tr style={{ color:'#94a3b8' }}>
                          <th style={{ textAlign:'left', padding:'6px 12px', fontWeight:600 }}>Date</th>
                          <th style={{ textAlign:'left', padding:'6px 12px', fontWeight:600 }}>Method</th>
                          <th style={{ textAlign:'left', padding:'6px 12px', fontWeight:600 }}>Note</th>
                          <th style={{ textAlign:'right', padding:'6px 12px', fontWeight:600 }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map((p,i) => (
                          <tr key={i} style={{ borderTop:'1px solid #f1f5f9' }}>
                            <td style={{ padding:'5px 12px', color:'#64748b' }}>{p.paidAt ? new Date(p.paidAt).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}) : ''}</td>
                            <td style={{ padding:'5px 12px', color:'#64748b', textTransform:'capitalize' }}>{p.method || '—'}</td>
                            <td style={{ padding:'5px 12px', color:'#64748b' }}>{p.note || '—'}</td>
                            <td style={{ padding:'5px 12px', textAlign:'right', fontWeight:700, color:'#16a34a' }}>₹{fmt(p.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}

              {/* Footer — table layout for print stability, matching the
                  pattern used elsewhere in this file */}
              <table style={{ width:'100%', borderCollapse:'collapse', marginTop:20, paddingTop:10, borderTop:'1px solid #e2e8f0', fontSize:10, color:'#94a3b8' }}>
                <tbody>
                  <tr>
                    <td style={{ padding:'10px 0 0', verticalAlign:'top', whiteSpace:'nowrap' }}>{CLINIC_NAME} — Thank you for your visit</td>
                    <td style={{ padding:'10px 0 0', verticalAlign:'top', textAlign:'right', whiteSpace:'nowrap' }}>
                      www.vijayasdentalcare.com
                    </td>
                  </tr>
                </tbody>
              </table>
            </>
          )}
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}
